import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import type { QuestionnairePreviewPass, TwinExposure } from '@digital-twins/shared-types';
import { previewQuestionnairePassesApi } from '../api/questionnaireDraftsApi';
import { getCompileErrorMessage } from '../utils/compileErrorMessage';
import { toast } from 'sonner';
import { Helpers, Model, Survey, type ItemValue, type Question } from 'survey-react';
import 'survey-react/defaultV2.min.css';
import { applyScalePresentation } from './scalePresentation';

type PreviewElement = { type?: unknown; name?: unknown; choicesOrder?: unknown; choices?: unknown; choicesFromQuestion?: unknown };

function choiceSource(element: PreviewElement, elements: Map<unknown, PreviewElement>): PreviewElement | undefined {
  let source: PreviewElement | undefined = element;
  const seen = new Set<unknown>();
  while (source && !Array.isArray(source.choices) && typeof source.choicesFromQuestion === 'string' && !seen.has(source.name)) {
    seen.add(source.name);
    source = elements.get(source.choicesFromQuestion);
  }
  return source;
}

/**
 * The lists twins read shuffled, each with the values of its choices that stay
 * last. The engine's rule (survey-runner `extractQuestionMetadata`, then
 * survey-engine `twin_choice_order`, its one owner):
 * - a list's order is its own `choicesOrder`, or, for a piped list
 *   (`choicesFromQuestion`) with none, its source's;
 * - a checkbox, radiogroup or dropdown is shuffled only when that order is
 *   "random";
 * - a shuffled list reads its choices that stay last (an `isExclusive`
 *   opt-out or a `keepLast` catch-all, `ChoiceOption.keepLast`) after the
 *   others, in their order.
 */
function listsTwinsShuffle(surveyJson: Record<string, unknown>): Map<string, Set<unknown>> {
  const pages = surveyJson.pages as Array<{ elements?: Array<PreviewElement> }> | undefined;
  const elements = new Map<unknown, PreviewElement>();
  pages?.forEach(page => page.elements?.forEach(element => elements.set(element.name, element)));
  const shuffled = new Map<string, Set<unknown>>();
  for (const element of elements.values()) {
    const source = choiceSource(element, elements);
    const order = typeof element.choicesOrder === 'string' ? element.choicesOrder : source?.choicesOrder;
    const twinsShuffle =
      (element.type === 'checkbox' || element.type === 'radiogroup' || element.type === 'dropdown') &&
      order === 'random';
    if (!twinsShuffle || typeof element.name !== 'string') continue;
    const choices: Array<unknown> = Array.isArray(source?.choices) ? source.choices : [];
    shuffled.set(
      element.name,
      new Set(choices.filter(staysLast).map(choice => (choice as { value?: unknown }).value)),
    );
  }
  return shuffled;
}

/**
 * The compiled survey with each grid twins read shuffled laid out in a shuffled
 * order. The engine's rule (`twin_choice_order`): a `matrix` or
 * `matrixdropdown` whose `rowsOrder` is "random" has its rows shuffled, and
 * one whose `columnsOrder` is "random" (a categorical grid's separate
 * categories) has its columns shuffled with the columns that stay last (an
 * `isExclusive` or `keepLast` one) after the others, in their order.
 * survey-core shuffles only a `matrix`'s rows and knows no `columnsOrder`, so
 * the preview lays both out at load with SurveyJS's own randomizer and drops
 * both keys, so a `matrix` is not shuffled twice.
 */
function gridsInTwinOrder(surveyJson: Record<string, unknown>): Record<string, unknown> {
  const pages = surveyJson.pages as Array<{ elements?: Array<Record<string, unknown>> }> | undefined;
  if (!Array.isArray(pages)) return surveyJson;
  return {
    ...surveyJson,
    pages: pages.map(page => ({
      ...page,
      elements: page.elements?.map(element => {
        if (element.type !== 'matrix' && element.type !== 'matrixdropdown') return element;
        const shuffleRows = element.rowsOrder === 'random' && Array.isArray(element.rows);
        const shuffleColumns = element.columnsOrder === 'random' && Array.isArray(element.columns);
        if (!shuffleRows && !shuffleColumns) return element;
        const laidOut: Record<string, unknown> = { ...element };
        if (shuffleRows) {
          laidOut.rows = Helpers.randomizeArray([...(element.rows as Array<unknown>)]);
          delete laidOut.rowsOrder;
        }
        if (shuffleColumns) {
          const columns = element.columns as Array<unknown>;
          laidOut.columns = [
            ...Helpers.randomizeArray(columns.filter(column => !staysLast(column))),
            ...columns.filter(staysLast),
          ];
          delete laidOut.columnsOrder;
        }
        return laidOut;
      }),
    })),
  };
}

/** The one "stays last" rule: an exclusive opt-out or a keep-last catch-all. */
function staysLast(item: unknown): boolean {
  const flags = item as { isExclusive?: unknown; keepLast?: unknown } | null;
  return flags?.isExclusive === true || flags?.keepLast === true;
}

/**
 * The compiled survey as the preview runs it, each list in the order twins
 * read it (`listsTwinsShuffle`). SurveyJS lays out a list whose `choicesOrder`
 * is "random" with its question's `randomizeArray`; each list twins shuffle
 * gets that order and a `randomizeArray` that shuffles with SurveyJS's own
 * randomizer and keeps its choices that stay last after the others. A piped
 * list is laid out from its source's choices at run time, so this is done on
 * the question, not the JSON. Every other list keeps its order. A grid's rows
 * are laid out by `gridsInTwinOrder`; scales and grids are presented by
 * `applyScalePresentation`.
 */
export function previewSurveyModel(compiledJson: Record<string, unknown>): Model {
  const surveyJson = gridsInTwinOrder(compiledJson);
  const shuffled = listsTwinsShuffle(surveyJson);
  const exclusiveChoices = new Map<string, Set<unknown>>();
  const pages = surveyJson.pages as Array<{ elements?: Array<PreviewElement> }> | undefined;
  const elements = new Map<unknown, PreviewElement>();
  pages?.forEach(page => page.elements?.forEach(element => elements.set(element.name, element)));
  elements.forEach(element => {
    if (element.type !== 'checkbox' || typeof element.name !== 'string') return;
    const choices = choiceSource(element, elements)?.choices;
    if (!Array.isArray(choices)) return;
    const exclusive = choices
      .filter(choice => (choice as { isExclusive?: unknown } | null)?.isExclusive === true)
      .map(choice => (choice as { value?: unknown }).value);
    if (exclusive.length) exclusiveChoices.set(element.name, new Set(exclusive));
  });
  const survey = new Model();
  // Keep Preview's answers consistent with the compiled choice-level exclusive
  // flags and the survey engine's typed constraint, including carried lists.
  survey.onValueChanging.add((_, event) => {
    const exclusive = exclusiveChoices.get(event.name);
    if (!exclusive || !Array.isArray(event.value)) return;
    const next = event.value as Array<unknown>;
    const previous = Array.isArray(event.oldValue) ? event.oldValue as Array<unknown> : [];
    const added = next.filter(value => !previous.includes(value));
    const addedExclusive = [...added].reverse().find(value => exclusive.has(value));
    if (addedExclusive !== undefined) {
      event.value = [addedExclusive];
    } else if (added.some(value => !exclusive.has(value))) {
      event.value = next.filter(value => !exclusive.has(value));
    } else if (next.length > 1 && next.some(value => exclusive.has(value))) {
      const selected = next.filter(value => exclusive.has(value));
      event.value = [selected[selected.length - 1]];
    }
  });
  survey.onQuestionCreated.add((_, { question }) => {
    const last = shuffled.get(question.name);
    if (!last) return;
    (question as unknown as { randomizeArray: (items: Array<ItemValue>) => Array<ItemValue> }).randomizeArray = items => [
      ...Helpers.randomizeArray(items.filter(item => !last.has(item.value))),
      ...items.filter(item => last.has(item.value)),
    ];
  });
  survey.fromJSON(surveyJson);
  shuffled.forEach((_, name) => {
    const question = survey.getQuestionByName(name) as unknown as { choicesOrder?: string } | null;
    if (question) question.choicesOrder = 'random';
  });
  // How each scale and grid is laid out (C04-2): runtime properties only.
  applyScalePresentation(survey, surveyJson);
  return survey;
}

/**
 * SurveyJS's colours, from the app's tokens, so the preview follows the light
 * and dark themes. Its theme falls back to `--primary`, `--background` and
 * other names the app defines as bare HSL channels (`171 86% 40%`), which are
 * not colours: a checked radio lost its fill and its dot. Its surfaces
 * (`-dim`, `-dim-light`, `-dark`) and muted text default to fixed light greys,
 * so in the dark theme typed answers were white on #f9f9f9 inside a #f3f3f3
 * page. Its own `--sjs-*` variables come first, so each is set here.
 */
const SURVEY_THEME: CSSProperties = {
  '--sjs-primary-backcolor': 'hsl(var(--primary))',
  '--sjs-primary-forecolor': 'hsl(var(--primary-foreground))',
  '--sjs-secondary-backcolor': 'hsl(var(--secondary))',
  '--sjs-general-backcolor': 'hsl(var(--background))',
  '--sjs-general-backcolor-dark': 'hsl(var(--accent))',
  '--sjs-general-backcolor-dim': 'hsl(var(--card))',
  '--sjs-general-backcolor-dim-light': 'hsl(var(--muted))',
  '--sjs-general-backcolor-dim-dark': 'hsl(var(--accent))',
  '--sjs-general-forecolor': 'hsl(var(--foreground))',
  '--sjs-general-forecolor-light': 'hsl(var(--muted-foreground))',
  '--sjs-general-dim-forecolor': 'hsl(var(--foreground))',
  '--sjs-general-dim-forecolor-light': 'hsl(var(--muted-foreground))',
  '--sjs-border-default': 'hsl(var(--border))',
  '--sjs-border-light': 'hsl(var(--border))',
  '--sjs-border-inside': 'hsl(var(--border))',
} as CSSProperties;

/** Where a question stands on the path the preview walked. */
type PathStatus =
  | 'answered'
  | 'notReachedAfterExit'
  | 'skippedByRoute'
  | 'notReachedYet'
  | 'hiddenByCondition'
  | 'shown'
  | 'skipped';

type PathState = { key: string; title: string; status: PathStatus };

/** One pass a respondent runs, as the engine partitions the survey. */

const NATIVE_DEPENDENCIES_PAGE = '__native_dependencies';
/** Each stimulus block's name prefix: one block before each question that shows assets. */
const STIMULUS_ELEMENT = '__preview_stimulus';

const isStimulusBlock = (name: unknown): boolean => String(name ?? '').startsWith(STIMULUS_ELEMENT);

function recordOf(value: unknown): Record<string, any> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, any> : {};
}

function escapeHtml(text: unknown): string {
  return String(text ?? '').replace(/[&<>"']/g, character => `&#${character.charCodeAt(0)};`);
}

function assetItems(pass: QuestionnairePreviewPass): Array<Record<string, any>> {
  const items = recordOf(recordOf(pass.surveyJson._canonicalConfig).assetContext).items;
  return Array.isArray(items) ? items.map(recordOf) : [];
}

/** The asset a lane pass is about, as the compile names it to twins. */
function laneAsset(pass: QuestionnairePreviewPass): Record<string, any> | undefined {
  return assetItems(pass).find(item => item.assetId === pass.laneAssetId);
}

/**
 * What a question of this pass puts on screen, read from the one per-question
 * reader the builder's strips read (shared-types `twinExposureOf`, pinned to
 * the engine by `tests/fixtures/study-spec/twin-exposure-cases.json`). A lane
 * pass shows its own asset only: the engine scopes the lane to it.
 */
function shownAt(pass: QuestionnairePreviewPass, element: Record<string, any>, exposure: TwinExposure | null): Array<string> {
  const questionMap = recordOf(recordOf(pass.surveyJson._canonicalConfig).questionMap);
  const ids = exposure?.onScreen.get(String(recordOf(questionMap[String(element.name)]).stableKey)) ?? [];
  return pass.phase === 'lane' ? ids.filter(id => id === pass.laneAssetId) : ids;
}

/** An asset's uploaded video, from the media the compile carries (https only, as images). */
function assetVideoUrl(asset: Record<string, any>): string | undefined {
  const media = Array.isArray(asset.media) ? asset.media.map(recordOf) : [];
  return media.find(ref => ref.kind === 'video' && String(ref.publicUrl ?? '').startsWith('https://'))?.publicUrl;
}

/**
 * The stimulus a question shows a twin, as one SurveyJS content element just
 * before it: each asset by its twin-facing name, its video or image when the
 * compile carries a public one, and the description the engine gives twins,
 * line breaks kept (B3: a pack's four claims read as four lines, as twins get
 * them). In the shared view each twin is shown the versions in its own order
 * (survey-engine `twin_stimulus_order`, balanced across twins), the same on
 * every question, so the preview lays them out in `order`, one shuffle per
 * pass, under `ownOrderLine`; a lane shows its one asset. The block is
 * shown exactly when its question is (the question's own `visibleIf`): a
 * question a condition hides puts nothing on screen. The set's shared context
 * (`assetContext.sharedContext`, the user's words that hold for every item)
 * opens the block, as the engine gives it to twins with every stimulus
 * (survey-engine `prompt_parts`; gate 2026-10-16, CT-07: twins read "Both: no
 * foreign transaction fees…" and the Preview showed it nowhere).
 */
function stimulusElement(
  pass: QuestionnairePreviewPass,
  question: Record<string, any>,
  assetIds: Array<string>,
  order: Array<string>,
  ownOrderLine: string,
): Record<string, unknown> | null {
  const assets = assetItems(pass);
  const images = recordOf(recordOf(pass.surveyJson._canonicalConfig).assetImageContext).items;
  const ownOrder = recordOf(question._canonical).exposureZone === 'shared_view' && assetIds.length > 1;
  const laidOut = ownOrder ? order.filter(id => assetIds.includes(id)) : assetIds;
  const html = laidOut.flatMap(assetId => {
    const asset = assets.find(item => item.assetId === assetId);
    if (!asset) return [];
    const video = assetVideoUrl(asset);
    const image = !video && Array.isArray(images)
      ? images.map(recordOf).find(item => item.assetId === assetId && String(item.publicUrl ?? '').startsWith('https://'))
      : undefined;
    return [
      `<p><strong>${escapeHtml(asset.twinFacingLabel ?? asset.title)}</strong></p>`,
      video ? `<video src="${escapeHtml(video)}" controls preload="metadata" style="max-width:100%"></video>` : '',
      image ? `<img src="${escapeHtml(image.publicUrl)}" alt="${escapeHtml(asset.title)}" style="max-width:100%" />` : '',
      asset.description ? `<p style="white-space:pre-line">${escapeHtml(asset.description)}</p>` : '',
    ];
  });
  if (html.length === 0) return null;
  const note = ownOrder ? `<p><em>${escapeHtml(ownOrderLine)}</em></p>` : '';
  const sharedContext = String(recordOf(recordOf(pass.surveyJson._canonicalConfig).assetContext).sharedContext ?? '').trim();
  const shared = sharedContext ? `<p style="white-space:pre-line">${escapeHtml(sharedContext)}</p>` : '';
  return {
    type: 'html',
    name: `${STIMULUS_ELEMENT}_${String(question.name)}`,
    ...(question.visibleIf ? { visibleIf: question.visibleIf } : {}),
    html: `<div data-preview-stimulus-order="${ownOrder ? 'own' : 'fixed'}">${shared}${note}${html.join('')}</div>`,
  };
}

/**
 * A pass's survey as the survey runner opens it: a lane or comparison pass
 * carries the shared pass's answers (`_nativeSessionContext`), whose question
 * definitions the runner puts on a hidden first page and whose answers it
 * restores as data (survey-runner `POST /sessions`). The stimulus shows just
 * before each question that puts it on screen (`shownAt`), and the questions
 * continue the builder's numbering (`questionStartIndex`).
 */
function passDefinition(
  pass: QuestionnairePreviewPass,
  exposure: TwinExposure | null,
  ownOrderLine = '',
): { definition: Record<string, any>; initialData: Record<string, unknown> } {
  const { _nativeSessionContext: context, ...definition } = pass.surveyJson as Record<string, any>;
  const dependencies = Array.isArray(recordOf(context).dependencyQuestions) ? recordOf(context).dependencyQuestions : [];
  const sourcePages: Array<Record<string, any>> = definition.pages ?? [];
  const shown = new Map(sourcePages
    .flatMap(page => (page.elements ?? []) as Array<Record<string, any>>)
    .map(element => [element, shownAt(pass, element, exposure)] as const));
  const order = Helpers.randomizeArray([...new Set([...shown.values()].flat())]);
  const pages = sourcePages.map(page => ({
    ...page,
    elements: ((page.elements ?? []) as Array<Record<string, any>>).flatMap(element => {
      const stimulus = stimulusElement(pass, element, shown.get(element) ?? [], order, ownOrderLine);
      return stimulus ? [stimulus, element] : [element];
    }),
  }));
  return {
    definition: {
      ...definition,
      questionStartIndex: String(pass.questionStartIndex),
      pages: [...(dependencies.length > 0 ? [{ name: NATIVE_DEPENDENCIES_PAGE, elements: dependencies }] : []), ...pages],
    },
    initialData: recordOf(recordOf(context).initialData),
  };
}

/** The questions a pass asks, in order: never its hidden dependencies or its stimulus. */
function passQuestions(survey: Model): Array<Question> {
  return survey.getAllQuestions().filter(question =>
    !isStimulusBlock(question.name) && question.page?.name !== NATIVE_DEPENDENCIES_PAGE);
}

/** How the path list names a pass's questions: a lane's by its asset, as twins meet it. */
function passPrefix(pass: QuestionnairePreviewPass): string {
  const asset = laneAsset(pass);
  return pass.phase === 'lane' && asset ? `${asset.twinFacingLabel ?? asset.title} · ` : '';
}

/** A pass not reached yet: its questions, each not reached. */
function unreachedStates(pass: QuestionnairePreviewPass, index: number): Array<PathState> {
  const { definition } = passDefinition(pass, null);
  return (definition.pages ?? [])
    .filter((page: Record<string, any>) => page.name !== NATIVE_DEPENDENCIES_PAGE)
    .flatMap((page: Record<string, any>) => page.elements ?? [])
    .filter((element: Record<string, any>) => !isStimulusBlock(element.name))
    .map((element: Record<string, any>) => ({
      key: `${index}:${element.name}`,
      title: `${passPrefix(pass)}${element.title || element.name}`,
      status: 'notReachedYet' as const,
    }));
}

/** Compiled native preview, isolated from respondent results and provider calls. */
/**
 * `locked` is the builder's one busy fact; while it holds, the button says
 * why with the panel's `lockedReason`, as its other locked controls do.
 * `language` is the saved draft's `contentLanguage`, the field the compile
 * writes the respondent instructions in: SurveyJS's own buttons and messages
 * follow it too. The builder's language chip is UI state and is not read.
 * The preview's own chrome (its button, the dialog's title, the path tracker
 * and the exit line) is the builder's, in the interface language
 * (`questionnaire.pathPreview.*`).
 *
 * The preview walks the passes a respondent runs, from the engine's own
 * partition (`previewQuestionnairePassesApi`): a lane study's shared pass,
 * one pass per tested asset with that asset's copy, then the comparison pass;
 * any other study is one pass. Each question shows the stimulus it puts on
 * screen for twins (`exposure`, the saved draft's `twinExposureOf`: the
 * preview opens only with no unsaved edits). The shared pass's answers seed
 * the passes after it, as the engine seeds them.
 */
export function QuestionnairePathPreview({ draftId, dirty, locked, lockedReason, language, exposure }: { draftId: string; dirty: boolean; locked?: boolean; lockedReason?: string; language?: string; exposure: TwinExposure | null }) {
  const { t } = useTranslation('app');
  const [model, setModel] = useState<Model | null>(null);
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState<Array<PathState>>([]);
  const [exited, setExited] = useState(false);
  useEffect(() => () => model?.dispose(), [model]);

  function runPass(passes: Array<QuestionnairePreviewPass>, index: number, walked: Array<PathState>) {
    const pass = passes[index];
    const { definition, initialData } = passDefinition(pass, exposure, t('questionnaire.pathPreview.stimulusOwnOrder'));
    const survey = previewSurveyModel({ ...definition, clearInvisibleValues: 'onHidden', ...(language ? { locale: language } : {}) });
    if (Object.keys(initialData).length > 0) {
      survey.data = initialData;
      // The first page is the first one the restored answers show: SurveyJS
      // picked it before them, so a pass that opens on a question gated on an
      // earlier pass's answer skipped it (net30 LP-23: Q4/Q5 "Skipped" for
      // the non-members they are for).
      survey.currentPageNo = 0;
    }
    // A pass with more passes after it ends on "Next", not "Complete" (U14).
    if (index + 1 < passes.length) survey.completeText = survey.pageNextText;
    const prefix = passPrefix(pass);
    const later = () => passes.slice(index + 1).flatMap((next, offset) => unreachedStates(next, index + 1 + offset));
    const shown = new Set<string>();
    // The furthest question the respondent has reached. A condition hides a
    // question only once the path gets there; before that it is not reached.
    // On the pass's last page nothing visible remains, so a later question
    // still hidden is hidden by its condition (U15).
    let furthest = -1;
    let invalidating = false;
    survey.onValueChanging.add((sender, event) => {
      if (invalidating || event.value === event.oldValue) return;
      const all = passQuestions(sender);
      const source = all.findIndex(question => question.name === event.name);
      if (source < 0) return;
      invalidating = true;
      try {
        for (const question of all.slice(source + 1)) {
          sender.clearValue(question.name);
          shown.delete(question.name);
        }
        furthest = Math.min(furthest, source);
      } finally { invalidating = false; }
    });
    const passStates = () => {
      const all = passQuestions(survey);
      for (const question of survey.currentPage?.questions ?? []) {
        if (question.isVisible) shown.add(question.name);
        furthest = Math.max(furthest, all.indexOf(question));
      }
      const triggers = (definition.triggers ?? []) as Array<{ type: string; expression: string; gotoName?: string; x_dt_routing?: { sourceName?: string; scope?: string } }>;
      const terminal = (survey.state === 'completed') ? triggers.find(trigger => trigger.type === 'complete' && survey.runExpression(trigger.expression) === true) : undefined;
      const sourceName = terminal?.x_dt_routing?.sourceName;
      const sourceIndex = all.findIndex(question => question.name === sourceName);
      const skipped = new Set<string>();
      for (const trigger of triggers) {
        if (trigger.type !== 'skip' || survey.runExpression(trigger.expression) !== true) continue;
        const from = all.findIndex(question => question.name === trigger.x_dt_routing?.sourceName);
        const to = all.findIndex(question => question.name === trigger.gotoName);
        if (from >= 0 && to > from) all.slice(from + 1, to).forEach(question => skipped.add(question.name));
      }
      const current = all.map((question, questionIndex): PathState => ({
        key: `${index}:${question.name}`,
        title: `${prefix}${question.title || question.name}`,
        // Answered: a value SurveyJS accepts (a half-filled required grid is not).
        status: !question.isEmpty() && !question.hasErrors(false) ? 'answered'
          : terminal && questionIndex > sourceIndex ? 'notReachedAfterExit'
          : skipped.has(question.name) ? 'skippedByRoute'
          : survey.state !== 'completed' && questionIndex > furthest && (question.isVisible || !survey.isLastPage) ? 'notReachedYet'
          : !question.isVisible ? 'hiddenByCondition'
          : shown.has(question.name) ? 'shown'
          : (survey.state === 'completed') ? 'skipped' : 'notReachedYet',
      }));
      return { current, terminal };
    };
    const update = () => {
      const { current } = passStates();
      setStates([...walked, ...current, ...later()]);
    };
    survey.onCurrentPageChanged.add(update);
    survey.onValueChanged.add(update);
    survey.onComplete.add(() => {
      const { current, terminal } = passStates();
      const done = [...walked, ...current];
      // An exit in a lane ends that lane unless it is scoped to the
      // respondent; an exit anywhere else ends the respondent (the engine's
      // `_run_monadic_internal`).
      const endsRespondent = terminal !== undefined
        && (pass.phase !== 'lane' || terminal.x_dt_routing?.scope === 'respondent');
      if (endsRespondent || index + 1 >= passes.length) {
        setExited(terminal !== undefined);
        const after = endsRespondent ? later().map(state => ({ ...state, status: 'notReachedAfterExit' as const })) : [];
        setStates([...done, ...after]);
        return;
      }
      if (pass.phase === 'shared') {
        // The passes after the shared one are seeded with its answers.
        void previewQuestionnairePassesApi(draftId, { priorAnswers: survey.data })
          .then(({ passes: seeded }) => runPass(seeded, index + 1, done))
          .catch(error => toast.error(getCompileErrorMessage(error, t('questionnaire.pathPreview.failed'), { draftId })));
        return;
      }
      runPass(passes, index + 1, done);
    });
    setModel(survey);
    update();
  }

  async function openPreview() {
    setLoading(true);
    setExited(false);
    try {
      const { passes } = await previewQuestionnairePassesApi(draftId);
      runPass(passes, 0, []);
    } catch (error) {
      // A compile refusal reads as its issue's reviewed line, filled from the
      // issue's typed facts (e.g. the asset no twin is shown).
      toast.error(getCompileErrorMessage(error, t('questionnaire.pathPreview.failed'), { draftId }));
    } finally {
      setLoading(false);
    }
  }
  return <>
    <Button type="button" variant="outline" size="sm" className="h-7 text-xs" disabled={locked || dirty || loading}
      title={locked ? lockedReason : dirty ? t('questionnaire.pathPreview.saveFirst') : t('questionnaire.pathPreview.tryIt')}
      onClick={() => void openPreview()} data-testid="questionnaire-path-preview">
      {loading ? t('questionnaire.pathPreview.preparing') : t('questionnaire.pathPreview.button')}
    </Button>
    <Dialog open={model !== null} onOpenChange={open => { if (!open) { setModel(null); } }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{t('questionnaire.pathPreview.title')}</DialogTitle>
          <DialogDescription>{t('questionnaire.pathPreview.description')}</DialogDescription></DialogHeader>
        {exited && <p role="status" className="rounded-md bg-muted p-3 text-sm">{t('questionnaire.pathPreview.exit')}</p>}
        {model && <div style={SURVEY_THEME}><Survey model={model} /></div>}
        <ol className="space-y-2 border-t pt-4 text-sm" aria-label={t('questionnaire.pathPreview.pathAria')}>
          {states.map(state => <li key={state.key}><span className="font-medium">{t(`questionnaire.pathPreview.status.${state.status}`)}</span> · {state.title}</li>)}
        </ol>
        <Button variant="outline" onClick={() => { void openPreview(); }}>{t('questionnaire.pathPreview.restart')}</Button>
      </DialogContent>
    </Dialog>
  </>;
}
